/**
 * Author-local sample preview — uses AudioContext BufferSource.
 * Does not touch the pattern transport / tracker playhead.
 */
export type PreviewOptions = {
  pcm: Float32Array;
  sampleRate: number;
  startFrame: number;
  endFrame: number;
  loop: boolean;
  loopStart?: number;
  loopEnd?: number;
  /** Equal-power crossfade frames for loop splice (optional). */
  loopXfade?: number;
  /** Playback rate (1 = native). */
  rate?: number;
  reverse?: boolean;
};

export type SamplePreviewHandle = {
  stop: () => void;
  /** Current read position in source frames (approximate). */
  getFrame: () => number;
  playing: () => boolean;
};

export function startSamplePreview(
  ctx: BaseAudioContext,
  options: PreviewOptions,
): SamplePreviewHandle {
  const {
    pcm,
    sampleRate,
    startFrame,
    endFrame,
    loop,
    rate = 1,
    reverse = false,
  } = options;

  const lo = Math.max(0, Math.min(pcm.length, Math.round(startFrame)));
  const hi = Math.max(lo + 1, Math.min(pcm.length, Math.round(endFrame)));
  const length = hi - lo;
  const slice = new Float32Array(length);
  if (reverse) {
    for (let i = 0; i < length; i++) {
      slice[i] = pcm[hi - 1 - i]!;
    }
  } else {
    slice.set(pcm.subarray(lo, hi));
  }

  let loopStartRel = Math.max(0, (options.loopStart ?? lo) - lo);
  let loopEndRel = Math.max(loopStartRel + 1, (options.loopEnd ?? hi) - lo);
  loopEndRel = Math.min(length, loopEndRel);
  loopStartRel = Math.min(loopStartRel, loopEndRel - 1);

  if (loop) {
    const loopLen = loopEndRel - loopStartRel;
    const maxXfade = Math.max(0, Math.floor(loopLen / 2) - 1);
    const xfade = Math.min(Math.max(0, Math.round(options.loopXfade ?? 0)), maxXfade);
    if (xfade > 0) {
      bakeLoopXfade(slice, loopStartRel, loopEndRel, xfade);
      // Match kernel wrap: skip overlapped head so WebAudio hard wrap is seamless.
      loopStartRel = Math.min(loopEndRel - 1, loopStartRel + xfade);
    }
  }

  const buffer = ctx.createBuffer(1, slice.length, sampleRate);
  buffer.copyToChannel(slice, 0);

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.playbackRate.value = Math.max(0.01, rate);
  source.loop = loop;
  if (loop) {
    source.loopStart = loopStartRel / sampleRate;
    source.loopEnd = Math.min(buffer.duration, loopEndRel / sampleRate);
  }

  const gain = ctx.createGain();
  gain.gain.value = 0.85;
  source.connect(gain);
  gain.connect(ctx.destination);

  const startedAt = ctx.currentTime;
  let stopped = false;
  source.onended = () => {
    stopped = true;
  };
  source.start();

  return {
    stop() {
      if (stopped) {
        return;
      }
      stopped = true;
      try {
        source.stop();
      } catch {
        // already stopped
      }
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        // ignore
      }
    },
    playing() {
      return !stopped;
    },
    getFrame() {
      if (stopped) {
        return reverse ? lo : hi;
      }
      const elapsed = (ctx.currentTime - startedAt) * sampleRate * Math.max(0.01, rate);
      if (loop && length > 0) {
        const loopLo = loopStartRel;
        const loopHi = loopEndRel;
        const loopLen = loopHi - loopLo;
        let pos = elapsed;
        if (pos >= loopLo) {
          pos = loopLo + ((pos - loopLo) % loopLen);
        }
        return reverse ? hi - 1 - (pos % length) : lo + pos;
      }
      const pos = Math.min(length, elapsed);
      return reverse ? hi - 1 - pos : lo + pos;
    },
  };
}

/** Destructive equal-power splice in the buffer so WebAudio loop wraps click less. */
function bakeLoopXfade(
  pcm: Float32Array,
  loopStart: number,
  loopEnd: number,
  xfade: number,
): void {
  for (let i = 0; i < xfade; i++) {
    const t = i / xfade;
    const outG = Math.cos(t * Math.PI * 0.5);
    const inG = Math.sin(t * Math.PI * 0.5);
    const outIdx = loopEnd - xfade + i;
    const inIdx = loopStart + i;
    if (outIdx < 0 || outIdx >= pcm.length || inIdx < 0 || inIdx >= pcm.length) {
      continue;
    }
    const a = pcm[outIdx]!;
    const b = pcm[inIdx]!;
    pcm[outIdx] = a * outG + b * inG;
  }
}
